import { useState, useRef, useEffect } from "react";
import { getUser, updateKYCProfile, updateProfile } from "../../api/apiServices";

export default function KYCPage() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("idle");
  const fileInputRef = useRef(null);
  const [uploadType, setUploadType] = useState("");

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    dob: "",
    country: "Nigeria",
    state: "",
    city: "", // Note: city isn't in your JSON, but kept for UI
    address: "",
    phone: "",
  });

  const [files, setFiles] = useState({
    photoID: null,
    utilityBill: null,
    avatar: null, // This will overwrite the existing profile picture URL
  });

  // 1. Fetch and Map Nested JSON Data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await getUser();
        const userData = response.user; // Accessing the 'user' object from your JSON

        setFormData({
          firstName: userData.firstName || "",
          lastName: userData.lastName || "",
          dob: userData.dob || "", 
          country: userData.country || "Nigeria",
          state: userData.location?.state || "",
          city: userData.location?.city || "", 
          address: userData.location?.address || "",
          phone: userData.phone || "",
        });
      } catch (err) {
        console.error("Error loading profile:", err);
      }
    };
    fetchUserData();
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // 2. Save Personal Info Step
  const handleProfileStep = async () => {
    setLoading(true);
    try {
      const data = new FormData();
      data.append("firstName", formData.firstName);
      data.append("lastName", formData.lastName);
      data.append("dob", formData.dob);
      data.append("phone", formData.phone);
      data.append("address", formData.address);
      data.append("state", formData.state);
      
      await updateKYCProfile(data);
      setStep(2);
    } catch (err) {
      alert("Failed to save personal info.");
    } finally {
      setLoading(false);
    }
  };

  // 3. Submit KYC Documents
  const handleSubmitFinal = async () => {
    setLoading(true);
    setSubmitStatus("submitting");
    try {
      const data = new FormData();
      
      // According to your backend: photoID and avatar are the keys
      if (files.photoID) data.append("photoID", files.photoID);
      if (files.avatar) data.append("avatar", files.avatar); // This is the selfie
      
      data.append("docType", "Government ID");
      data.append("phoneNumber", formData.phone);
      data.append("country", formData.country);

      await updateKYCProfile(data);
      setSubmitStatus("success");
    } catch (err) {
      setSubmitStatus("idle");
      alert("KYC Upload failed.");
    } finally {
      setLoading(false);
    }
  };

  const isStep1Valid = formData.firstName && formData.lastName && formData.dob && formData.phone;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center">
      <div className="w-full max-w-2xl bg-white min-h-screen flex flex-col shadow-sm relative">
        
        {/* Progress Header */}
        <div className="px-5 py-6 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <h1 className="font-bold text-slate-900" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Identity Verification</h1>
          <div className="flex gap-1">
            {[1, 2, 3].map(i => (
              <div key={i} className={`h-1.5 w-6 rounded-full ${step >= i ? "bg-emerald-500" : "bg-slate-100"}`} />
            ))}
          </div>
        </div>

        <div className="flex-1 px-5 py-8">
          {step === 1 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase">First Name</label>
                  <input name="firstName" value={formData.firstName} onChange={handleInputChange} className="w-full p-4 border rounded-xl mt-1" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Last Name</label>
                  <input name="lastName" value={formData.lastName} onChange={handleInputChange} className="w-full p-4 border rounded-xl mt-1" />
                </div>
              </div>
              
              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase">Phone Number</label>
                <input name="phone" value={formData.phone} onChange={handleInputChange} className="w-full p-4 border rounded-xl mt-1" />
              </div>

              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase">Date of Birth</label>
                <input name="dob" type="date" value={formData.dob} onChange={handleInputChange} className="w-full p-4 border rounded-xl mt-1" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase">State</label>
                  <input name="state" value={formData.state} onChange={handleInputChange} className="w-full p-4 border rounded-xl mt-1" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Country</label>
                  <input value={formData.country} readOnly className="w-full p-4 border rounded-xl mt-1 bg-slate-50 text-slate-400" />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase">Home Address</label>
                <textarea name="address" value={formData.address} onChange={handleInputChange} className="w-full p-4 border rounded-xl mt-1 h-24" />
              </div>

              <button disabled={!isStep1Valid || loading} onClick={handleProfileStep} className="w-full py-4 bg-slate-900 text-white rounded-xl font-bold transition-all active:scale-[0.98]">
                {loading ? "Saving Details..." : "Continue"}
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4">
              <input ref={fileInputRef} type="file" className="hidden" onChange={(e) => {
                const file = e.target.files[0];
                if (file) setFiles(prev => ({ ...prev, [uploadType]: file }));
              }} />
              
              <div className="p-5 bg-blue-50 rounded-2xl flex gap-3 border border-blue-100">
                <i className="ri-information-line text-blue-600 text-xl" />
                <p className="text-xs text-blue-700 leading-relaxed">Please upload clear documents. Blurry photos will be rejected by our manual review team.</p>
              </div>

              <div className="space-y-3">
                <UploadBox 
                  label="Government Issued ID" 
                  sub="NIN, Voter's Card, or Passport" 
                  icon="ri-id-card-line"
                  active={files.photoID}
                  onClick={() => { setUploadType("photoID"); fileInputRef.current.click(); }}
                />
                <UploadBox 
                  label="Liveness Check (Selfie)" 
                  sub="Clear photo of your face" 
                  icon="ri-camera-user-line"
                  active={files.avatar}
                  onClick={() => { setUploadType("avatar"); fileInputRef.current.click(); }}
                />
              </div>

              <button disabled={!files.photoID || !files.avatar} onClick={() => setStep(3)} className="w-full py-4 bg-slate-900 text-white rounded-xl font-bold">
                Review Application
              </button>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4">
               <div className="rounded-2xl border border-slate-100 overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b border-slate-100">
                    <p className="text-[10px] font-black uppercase text-slate-400">Submission Summary</p>
                  </div>
                  <div className="p-4 space-y-3 text-sm">
                    <div className="flex justify-between"><span className="text-slate-400">Name</span><span className="font-bold">{formData.firstName} {formData.lastName}</span></div>
                    <div className="flex justify-between"><span className="text-slate-400">Address</span><span className="font-bold text-right ml-4">{formData.address}</span></div>
                    <div className="flex justify-between"><span className="text-slate-400">Documents</span><span className="text-emerald-600 font-bold">2 Files Attached</span></div>
                  </div>
               </div>

               <button onClick={handleSubmitFinal} className="w-full py-4 bg-emerald-600 text-white rounded-xl font-bold flex items-center justify-center gap-2">
                 {loading ? <i className="ri-loader-4-line animate-spin" /> : "Submit for Manual Review"}
               </button>
            </div>
          )}
        </div>

        {/* Success Modal */}
        {submitStatus === "success" && (
          <div className="absolute inset-0 bg-white z-50 flex items-center justify-center p-8 text-center animate-in fade-in zoom-in duration-300">
            <div>
              <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-check-double-line text-4xl" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Under Review</h2>
              <p className="text-slate-500 mt-2 mb-8">We've received your KYC documents. Your account status will update within 24 hours.</p>
              <button onClick={() => window.location.href = "/home"} className="w-full py-4 bg-slate-900 text-white rounded-xl font-bold">Finish</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Simple helper component for upload boxes
function UploadBox({ label, sub, icon, active, onClick }) {
  return (
    <div onClick={onClick} className={`p-4 rounded-2xl border-2 border-dashed cursor-pointer flex items-center justify-between transition-all ${active ? "border-emerald-500 bg-emerald-50/50" : "border-slate-200 hover:border-slate-300"}`}>
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${active ? "bg-emerald-100 text-emerald-600" : "bg-slate-50 text-slate-400"}`}>
          <i className={`${icon} text-xl`} />
        </div>
        <div>
          <p className="text-sm font-bold text-slate-800">{label}</p>
          <p className="text-[11px] text-slate-400">{active ? "Document selected" : sub}</p>
        </div>
      </div>
      <i className={active ? "ri-checkbox-circle-fill text-emerald-500 text-xl" : "ri-add-circle-line text-slate-300 text-xl"} />
    </div>
  );
}